import React, { useState, useEffect, useMemo } from 'react';
import { Link2, Copy, Share2, Users, TrendingUp, Loader, ToggleLeft, ToggleRight, Filter } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { format, startOfDay, startOfWeek, startOfMonth, parseISO } from 'date-fns';
import DatePicker from 'react-datepicker';

interface Lead {
  id: string;
  client_name: string;
  cpf: string;
  phone: string;
  status: string;
  rejection_reason: string | null;
  rejected_by: string | null;
  seller_id: string | null;
  contract_id: string | null;
  first_contact_at: string | null;
  converted_at: string | null;
  created_at: string;
}

interface SellerConfig {
  id: string;
  seller_id: string;
  is_receiving_leads: boolean;
  leads_in_current_batch: number;
  queue_position: number;
  seller_name?: string;
}

interface UnitConfig {
  id: string;
  unit_id: string;
  slug: string;
  is_active: boolean;
  access_count: number;
}

type PeriodFilter = 'today' | 'week' | 'month' | 'custom';

export default function LeadsManager() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [unitConfig, setUnitConfig] = useState<UnitConfig | null>(null);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [sellerConfigs, setSellerConfigs] = useState<SellerConfig[]>([]);
  const [sellers, setSellers] = useState<{ id: string; display_name: string | null; email: string }[]>([]);
  const [periodFilter, setPeriodFilter] = useState<PeriodFilter>('month');
  const [customStart, setCustomStart] = useState<Date | null>(null);
  const [customEnd, setCustomEnd] = useState<Date | null>(null);
  const [sellerFilter, setSellerFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [activeView, setActiveView] = useState<'dashboard' | 'list' | 'funnel'>('dashboard');

  const unitId = user?.unit_id;

  useEffect(() => {
    if (unitId) {
      loadData();
    }
  }, [unitId]);

  const loadData = async () => {
    setLoading(true);
    await Promise.all([loadUnitConfig(), loadLeads(), loadSellerConfigs(), loadSellers()]);
    setLoading(false);
  };

  const loadUnitConfig = async () => {
    const { data } = await supabase
      .from('unit_lead_config')
      .select('*')
      .eq('unit_id', unitId!)
      .maybeSingle();

    if (data) {
      setUnitConfig(data);
    } else {
      const unitRes = await supabase.from('units').select('name').eq('id', unitId!).maybeSingle();
      const unitName = unitRes.data?.name || 'unidade';
      const slug = unitName.toLowerCase()
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

      const { data: newConfig } = await supabase
        .from('unit_lead_config')
        .insert({ unit_id: unitId!, slug })
        .select()
        .single();

      setUnitConfig(newConfig);
    }
  };

  const loadLeads = async () => {
    const { data } = await supabase
      .from('leads')
      .select('*')
      .eq('unit_id', unitId!)
      .order('created_at', { ascending: false });
    setLeads(data || []);
  };

  const loadSellerConfigs = async () => {
    const { data } = await supabase
      .from('lead_seller_config')
      .select('*')
      .eq('unit_id', unitId!)
      .order('queue_position');
    setSellerConfigs(data || []);
  };

  const loadSellers = async () => {
    const { data } = await supabase
      .from('users')
      .select('id, display_name, email')
      .eq('unit_id', unitId!)
      .eq('role', 'seller')
      .order('created_at', { ascending: true });
    setSellers(data || []);
  };

  const toggleSellerReceiving = async (config: SellerConfig) => {
    await supabase
      .from('lead_seller_config')
      .update({ is_receiving_leads: !config.is_receiving_leads })
      .eq('id', config.id);
    loadSellerConfigs();
  };

  const addSellerToLeads = async (sellerId: string) => {
    const maxPos = sellerConfigs.reduce((max, c) => Math.max(max, c.queue_position), 0);
    await supabase.from('lead_seller_config').insert({
      unit_id: unitId!,
      seller_id: sellerId,
      queue_position: maxPos + 1,
    });
    loadSellerConfigs();
  };

  const copyLink = () => {
    if (!unitConfig) return;
    const domain = import.meta.env.VITE_LEADS_DOMAIN || window.location.origin;
    const url = `${domain}/leads/${unitConfig.slug}`;
    navigator.clipboard.writeText(url);
  };

  const shareWhatsapp = () => {
    if (!unitConfig) return;
    const domain = import.meta.env.VITE_LEADS_DOMAIN || window.location.origin;
    const url = `${domain}/leads/${unitConfig.slug}`;
    const text = encodeURIComponent(`Faca seu cadastro rapido aqui: ${url}`);
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  const getDateFilter = (): Date | null => {
    const now = new Date();
    if (periodFilter === 'today') return startOfDay(now);
    if (periodFilter === 'week') return startOfWeek(now, { weekStartsOn: 1 });
    if (periodFilter === 'month') return startOfMonth(now);
    if (periodFilter === 'custom') return customStart;
    return null;
  };

  const filteredLeads = useMemo(() => {
    let filtered = leads;
    const dateFrom = getDateFilter();
    if (dateFrom) {
      filtered = filtered.filter(l => {
        const createdInPeriod = new Date(l.created_at) >= dateFrom;
        const convertedInPeriod = l.converted_at && new Date(l.converted_at) >= dateFrom;
        return createdInPeriod || convertedInPeriod;
      });
    }
    if (periodFilter === 'custom' && customEnd) {
      const endDate = new Date(customEnd);
      endDate.setHours(23, 59, 59, 999);
      filtered = filtered.filter(l => {
        const createdInPeriod = new Date(l.created_at) <= endDate;
        const convertedInPeriod = l.converted_at && new Date(l.converted_at) <= endDate;
        return createdInPeriod || convertedInPeriod;
      });
    }
    if (sellerFilter !== 'all') {
      filtered = filtered.filter(l => l.seller_id === sellerFilter);
    }
    if (statusFilter !== 'all') {
      filtered = filtered.filter(l => l.status === statusFilter);
    }
    return filtered;
  }, [leads, periodFilter, customStart, customEnd, sellerFilter, statusFilter]);

  const metrics = useMemo(() => {
    const total = filteredLeads.length;
    const distributed = filteredLeads.filter(l => l.status !== 'new' && l.status !== 'rejected_auto').length;
    const inContact = filteredLeads.filter(l => l.status === 'in_contact').length;
    const awaitingReview = filteredLeads.filter(l => l.status === 'awaiting_review').length;
    const approved = filteredLeads.filter(l => l.status === 'approved').length;
    const rejSeller = filteredLeads.filter(l => l.status === 'rejected_seller').length;
    const rejManager = filteredLeads.filter(l => l.status === 'rejected_manager').length;
    const rejAuto = filteredLeads.filter(l => l.status === 'rejected_auto').length;
    const conversionRate = total > 0 ? ((approved / total) * 100).toFixed(1) : '0';
    return { total, distributed, inContact, awaitingReview, approved, rejSeller, rejManager, rejAuto, conversionRate };
  }, [filteredLeads]);

  const sellerMetrics = useMemo(() => {
    return sellers.map(seller => {
      const sLeads = filteredLeads.filter(l => l.seller_id === seller.id);
      const received = sLeads.length;
      const contacted = sLeads.filter(l => l.first_contact_at).length;
      const rejected = sLeads.filter(l => l.status === 'rejected_seller').length;
      const contracts = sLeads.filter(l => l.contract_id).length;
      const approved = sLeads.filter(l => l.status === 'approved').length;
      const rate = received > 0 ? ((approved / received) * 100).toFixed(1) : '0';
      return { ...seller, received, contacted, rejected, contracts, approved, rate };
    });
  }, [sellers, filteredLeads]);

  const funnelData = useMemo(() => {
    const total = filteredLeads.length;
    const distributed = filteredLeads.filter(l => !['new', 'rejected_auto'].includes(l.status)).length;
    const contacted = filteredLeads.filter(l => l.first_contact_at).length;
    const withContract = filteredLeads.filter(l => l.contract_id).length;
    const approved = filteredLeads.filter(l => l.status === 'approved').length;
    return [
      { label: 'Captados', value: total },
      { label: 'Distribuidos', value: distributed },
      { label: 'Atendidos', value: contacted },
      { label: 'Contrato Criado', value: withContract },
      { label: 'Aprovados', value: approved },
    ];
  }, [filteredLeads]);

  const getSellerName = (sellerId: string | null) => {
    if (!sellerId) return '-';
    const seller = sellers.find(s => s.id === sellerId);
    return seller?.display_name || seller?.email?.split('@')[0] || '-';
  };

  const statusLabels: Record<string, string> = {
    new: 'Novo',
    distributed: 'Distribuido',
    in_contact: 'Em Atendimento',
    awaiting_review: 'Aguardando Revisao',
    approved: 'Aprovado',
    rejected_seller: 'Recusado (Vendedor)',
    rejected_manager: 'Recusado (Gestor)',
    rejected_auto: 'Recusado (Auto)',
  };

  const statusColors: Record<string, string> = {
    new: 'bg-gray-100 text-gray-700',
    distributed: 'bg-blue-100 text-blue-700',
    in_contact: 'bg-yellow-100 text-yellow-700',
    awaiting_review: 'bg-orange-100 text-orange-700',
    approved: 'bg-green-100 text-green-700',
    rejected_seller: 'bg-red-100 text-red-700',
    rejected_manager: 'bg-red-100 text-red-700',
    rejected_auto: 'bg-red-50 text-red-600',
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">Painel de Leads</h1>

      {/* Link Card */}
      {unitConfig && (
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-6 text-white">
          <div className="flex items-center gap-2 mb-3">
            <Link2 className="w-5 h-5" />
            <h2 className="font-semibold text-lg">Link Publico de Captacao</h2>
          </div>
          <p className="text-blue-100 text-sm mb-3 font-mono break-all">
            {import.meta.env.VITE_LEADS_DOMAIN || window.location.origin}/leads/{unitConfig.slug}
          </p>
          <div className="flex flex-wrap gap-4 text-sm text-blue-100 mb-4">
            <span>{unitConfig.access_count} acessos</span>
            <span>{metrics.total} captados</span>
            <span>{metrics.rejAuto} recusados auto</span>
            <span>{metrics.approved} convertidos</span>
          </div>
          <div className="flex gap-2">
            <button onClick={copyLink} className="flex items-center gap-1.5 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg text-sm transition">
              <Copy className="w-4 h-4" /> Copiar Link
            </button>
            <button onClick={shareWhatsapp} className="flex items-center gap-1.5 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg text-sm transition">
              <Share2 className="w-4 h-4" /> WhatsApp
            </button>
          </div>
        </div>
      )}

      {/* Seller Control */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <Users className="w-5 h-5 text-gray-500" /> Vendedores Recebendo Leads
        </h3>
        <div className="space-y-2">
          {[...sellerConfigs].sort((a, b) => {
            const ia = sellers.findIndex(s => s.id === a.seller_id);
            const ib = sellers.findIndex(s => s.id === b.seller_id);
            return (ia === -1 ? Infinity : ia) - (ib === -1 ? Infinity : ib);
          }).map(config => {
            const seller = sellers.find(s => s.id === config.seller_id);
            const name = seller?.display_name || seller?.email?.split('@')[0] || 'Vendedor';
            return (
              <div key={config.id} className="flex items-center justify-between py-2 px-3 rounded-lg bg-gray-50">
                <div>
                  <span className="font-medium text-gray-800">{name}</span>
                  <span className="text-xs text-gray-500 ml-2">({config.leads_in_current_batch}/2)</span>
                </div>
                <button onClick={() => toggleSellerReceiving(config)}>
                  {config.is_receiving_leads ? (
                    <ToggleRight className="w-8 h-8 text-green-500" />
                  ) : (
                    <ToggleLeft className="w-8 h-8 text-gray-400" />
                  )}
                </button>
              </div>
            );
          })}
          {sellers.filter(s => !sellerConfigs.find(c => c.seller_id === s.id)).map(seller => (
            <div key={seller.id} className="flex items-center justify-between py-2 px-3 rounded-lg border border-dashed border-gray-300">
              <span className="text-gray-600">{seller.display_name || seller.email.split('@')[0]}</span>
              <button
                onClick={() => addSellerToLeads(seller.id)}
                className="text-sm px-3 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
              >
                Ativar
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex flex-wrap items-center gap-3">
          <Filter className="w-4 h-4 text-gray-500" />
          <select value={periodFilter} onChange={e => setPeriodFilter(e.target.value as PeriodFilter)} className="text-sm border border-gray-300 rounded-lg px-3 py-1.5">
            <option value="today">Hoje</option>
            <option value="week">Semana</option>
            <option value="month">Mes</option>
            <option value="custom">Personalizado</option>
          </select>
          {periodFilter === 'custom' && (
            <div className="flex items-center gap-2">
              <DatePicker selected={customStart} onChange={d => setCustomStart(d)} dateFormat="dd/MM/yyyy" placeholderText="De" className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 w-28" />
              <DatePicker selected={customEnd} onChange={d => setCustomEnd(d)} dateFormat="dd/MM/yyyy" placeholderText="Ate" className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 w-28" />
            </div>
          )}
          <select value={sellerFilter} onChange={e => setSellerFilter(e.target.value)} className="text-sm border border-gray-300 rounded-lg px-3 py-1.5">
            <option value="all">Todos Vendedores</option>
            {sellers.map(s => (
              <option key={s.id} value={s.id}>{s.display_name || s.email.split('@')[0]}</option>
            ))}
          </select>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="text-sm border border-gray-300 rounded-lg px-3 py-1.5">
            <option value="all">Todos Status</option>
            {Object.entries(statusLabels).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
        </div>
      </div>

      {/* View Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl">
        {[
          { key: 'dashboard', label: 'Metricas' },
          { key: 'list', label: 'Lista Completa' },
          { key: 'funnel', label: 'Funil' },
        ].map(v => (
          <button
            key={v.key}
            onClick={() => setActiveView(v.key as typeof activeView)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
              activeView === v.key ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {v.label}
          </button>
        ))}
      </div>

      {/* Dashboard View */}
      {activeView === 'dashboard' && (
        <div className="space-y-6">
          {/* Metric Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {[
              { label: 'Captados', value: metrics.total, color: 'text-gray-900' },
              { label: 'Distribuidos', value: metrics.distributed, color: 'text-blue-600' },
              { label: 'Em Atendimento', value: metrics.inContact, color: 'text-yellow-600' },
              { label: 'Ag. Revisao', value: metrics.awaitingReview, color: 'text-orange-600' },
              { label: 'Aprovados', value: metrics.approved, color: 'text-green-600' },
              { label: 'Rec. Vendedor', value: metrics.rejSeller, color: 'text-red-500' },
              { label: 'Rec. Gestor', value: metrics.rejManager, color: 'text-red-600' },
              { label: 'Rec. Auto', value: metrics.rejAuto, color: 'text-red-400' },
              { label: 'Conversao', value: `${metrics.conversionRate}%`, color: 'text-green-700' },
            ].map(m => (
              <div key={m.label} className="bg-white rounded-xl border border-gray-200 p-4">
                <p className="text-xs text-gray-500 mb-1">{m.label}</p>
                <p className={`text-2xl font-bold ${m.color}`}>{m.value}</p>
              </div>
            ))}
          </div>

          {/* Seller Metrics Table */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="p-4 border-b border-gray-100">
              <h3 className="font-semibold text-gray-900">Metricas por Vendedor</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Vendedor</th>
                    <th className="text-center px-3 py-3 font-medium text-gray-600">Recebidos</th>
                    <th className="text-center px-3 py-3 font-medium text-gray-600">Atendidos</th>
                    <th className="text-center px-3 py-3 font-medium text-gray-600">Recusados</th>
                    <th className="text-center px-3 py-3 font-medium text-gray-600">Contratos</th>
                    <th className="text-center px-3 py-3 font-medium text-gray-600">Aprovados</th>
                    <th className="text-center px-3 py-3 font-medium text-gray-600">Conversao</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {sellerMetrics.map(sm => (
                    <tr key={sm.id}>
                      <td className="px-4 py-3 font-medium text-gray-800">{sm.display_name || sm.email.split('@')[0]}</td>
                      <td className="text-center px-3 py-3">{sm.received}</td>
                      <td className="text-center px-3 py-3">{sm.contacted}</td>
                      <td className="text-center px-3 py-3 text-red-600">{sm.rejected}</td>
                      <td className="text-center px-3 py-3">{sm.contracts}</td>
                      <td className="text-center px-3 py-3 text-green-600">{sm.approved}</td>
                      <td className="text-center px-3 py-3 font-medium">{sm.rate}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* List View */}
      {activeView === 'list' && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Nome</th>
                  <th className="text-left px-3 py-3 font-medium text-gray-600">CPF</th>
                  <th className="text-left px-3 py-3 font-medium text-gray-600">Telefone</th>
                  <th className="text-left px-3 py-3 font-medium text-gray-600">Vendedor</th>
                  <th className="text-left px-3 py-3 font-medium text-gray-600">Status</th>
                  <th className="text-left px-3 py-3 font-medium text-gray-600">Motivo</th>
                  <th className="text-left px-3 py-3 font-medium text-gray-600">Entrada</th>
                  <th className="text-left px-3 py-3 font-medium text-gray-600">1o Contato</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredLeads.map(lead => (
                  <tr key={lead.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-900 max-w-[160px] truncate">{lead.client_name}</td>
                    <td className="px-3 py-3 text-gray-600 font-mono text-xs">{lead.cpf}</td>
                    <td className="px-3 py-3 text-gray-600">{lead.phone.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3')}</td>
                    <td className="px-3 py-3 text-gray-600">{getSellerName(lead.seller_id)}</td>
                    <td className="px-3 py-3">
                      <span className={`text-xs px-2 py-1 rounded-full font-medium ${statusColors[lead.status] || ''}`}>
                        {statusLabels[lead.status] || lead.status}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-gray-500 text-xs max-w-[140px] truncate">{lead.rejection_reason || '-'}</td>
                    <td className="px-3 py-3 text-gray-500 text-xs">{format(new Date(lead.created_at), 'dd/MM HH:mm')}</td>
                    <td className="px-3 py-3 text-gray-500 text-xs">{lead.first_contact_at ? format(new Date(lead.first_contact_at), 'dd/MM HH:mm') : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredLeads.length === 0 && (
              <p className="text-center text-gray-500 py-8">Nenhum lead encontrado com os filtros selecionados.</p>
            )}
          </div>
        </div>
      )}

      {/* Funnel View */}
      {activeView === 'funnel' && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="font-semibold text-gray-900 mb-6 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-gray-500" /> Funil de Conversao
          </h3>
          <div className="space-y-3">
            {funnelData.map((step, i) => {
              const maxValue = funnelData[0].value || 1;
              const width = Math.max((step.value / maxValue) * 100, 8);
              const percentage = funnelData[0].value > 0 ? ((step.value / funnelData[0].value) * 100).toFixed(1) : '0';
              return (
                <div key={step.label} className="flex items-center gap-4">
                  <span className="w-32 text-sm text-gray-600 text-right flex-shrink-0">{step.label}</span>
                  <div className="flex-1 relative">
                    <div
                      className="h-10 rounded-lg flex items-center px-3 transition-all"
                      style={{
                        width: `${width}%`,
                        backgroundColor: ['#3b82f6', '#2563eb', '#f59e0b', '#f97316', '#10b981'][i],
                      }}
                    >
                      <span className="text-white text-sm font-semibold">{step.value}</span>
                    </div>
                  </div>
                  <span className="w-14 text-sm text-gray-500 flex-shrink-0">{percentage}%</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
